import React, { useState, useMemo } from 'react';
import ReportPage from '../../../Components/crude/ReportPage';
import useReportData from '../../../Components/crude/useReportData';
import {
  REPORT_ENDPOINTS,
  EMPTY_REPORT_FILTERS,
  originShortLabel,
} from '../../../Components/crude/reportConstants';

// Monitoring Report — GET /v1/Report/GetMonitoringReports (rhacrude_api).
//
// The Evaluation Stage columns are dynamic: one group per entry in each row's
// `evaluations[]` (sourced from master EvaluationDocument, ordered by
// orderNumber). Each stage renders its document's Eff Date plus the stage's
// Duration (Days).
//
// `durationDays` is computed BY THE BACKEND (per the 2026-07-14 change) — the
// FE must NOT recompute date differences the way the old Blazor page did.
// `null` renders as "-".
//
// Sortable: the 7 displayed columns + Origin (8 fields). The dynamic per-stage
// columns are not sortable (sending one returns 400).
const MonitoringReportView = () => {
  const [filters, setFilters] = useState(EMPTY_REPORT_FILTERS);

  const {
    items,
    totalRows,
    loading,
    page,
    perPage,
    sortField,
    sortDirection,
    setPage,
    setPerPage,
    handleSort,
  } = useReportData({ endpoint: REPORT_ENDPOINTS.MONITORING.LIST, filters });

  const columns = useMemo(() => {
    // The stage skeleton is identical across rows in one response, so derive
    // the columns from the first row. Re-derived on every fetch, because a
    // Plant filter can change which stages come back.
    const stages = [...(items[0]?.evaluations ?? [])].sort(
      (a, b) => (a.orderNumber || 0) - (b.orderNumber || 0)
    );

    const stageColumns = stages.map(stage => {
      const entryFor = row =>
        row.evaluations?.find(e => e.id === stage.id) ?? null;

      return {
        header: stage.evaluationName,
        children: [
          {
            key: `${stage.id}-date`,
            header: 'Eff Date',
            sortable: false,
            align: 'center',
            accessor: row => {
              // Multiple documents can be uploaded to one stage; the stage's
              // completion date is the latest EffDate among them.
              const docs = entryFor(row)?.evaluationDocuments ?? [];
              if (!docs.length) return '-';
              const latest = docs
                .map(d => d.effDate)
                .filter(Boolean)
                .sort()
                .pop();
              return latest ? String(latest).slice(0, 10) : '-';
            },
          },
          {
            key: `${stage.id}-duration`,
            header: 'Duration (Days)',
            sortable: false,
            align: 'center',
            // Server-computed. Do not recalculate here.
            accessor: row => entryFor(row)?.durationDays ?? '-',
          },
        ],
      };
    });

    return [
      {
        key: 'CrudeName',
        header: 'Nama Minyak Mentah',
        accessor: row => row.crudeName,
      },
      {
        key: 'Origin',
        header: 'DOMP/IMP',
        accessor: row => originShortLabel(row.origin),
        align: 'center',
      },
      { key: 'Country', header: 'Asal Negara', accessor: row => row.country },
      {
        key: 'AssayYear',
        header: 'Tahun Assay',
        accessor: row => row.assayYear,
        align: 'center',
      },
      {
        key: 'ProductionRate',
        header: 'Production Rate (MBCD)',
        accessor: row => row.productionRate,
        align: 'center',
      },
      {
        key: 'ParcelSize',
        header: 'Parcel Size (MB)',
        accessor: row => row.parcelSize,
        align: 'center',
      },
      {
        key: 'Penyaluran',
        header: 'Penyaluran Via',
        accessor: row => row.penyaluran,
      },
      ...stageColumns,
    ];
  }, [items]);

  return (
    <ReportPage
      title='Monitoring Report'
      cardTitle='Monitoring Report'
      documentTitle='Monitoring Report'
      columns={columns}
      data={items}
      loading={loading}
      totalRows={totalRows}
      page={page}
      perPage={perPage}
      sortField={sortField}
      sortDirection={sortDirection}
      onChangePage={setPage}
      onChangeRowsPerPage={newPerPage => {
        setPerPage(newPerPage);
        setPage(1);
      }}
      onSort={handleSort}
      filters={filters}
      onFiltersChange={setFilters}
      endpoints={REPORT_ENDPOINTS.MONITORING}
      exportBaseName='monitoring-report'
      emptyText='No monitoring report data found'
    />
  );
};

export default MonitoringReportView;
